<template>
  <v-container fluid>
    <v-toolbar flat class="d-print-none">
      <v-toolbar-title>Active Users</v-toolbar-title>
      <v-spacer/>
      <span v-if="users" class="text--secondary mr-4" data-cy="active-summary">
        {{ counts.active }} active, {{ counts.away }} away, {{ counts.gone }} gone
      </span>
      <v-switch v-model="showGone" label="Show gone" hide-details dense class="mt-0 mr-4"/>
      <v-btn outlined :loading="loading" @click="fetchUsers">
        <v-icon left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </v-toolbar>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <p class="caption text--secondary">
      Judged by api calls: active means a call in the last {{ minutes(activeMs) }} minutes, away in the last
      {{ minutes(awayMs) }} minutes, gone otherwise. A page that refreshes itself keeps its tab active while
      nobody is using it. The api only remembers calls since it last restarted, and forgets after a day.
      Refreshes every {{ refreshSeconds }} seconds.
    </p>

    <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="key" sort-by="lastSeenAt"
                  sort-desc :items-per-page="-1" hide-default-footer dense data-cy="active-users">
      <template v-slot:item.status="{item}">
        <v-chip x-small label :color="statusColors[item.status]" dark>{{ item.status }}</v-chip>
      </template>
      <template v-slot:item.machine="{item}">
        <strong v-if="item.isThisMachine" title="This machine">{{ item.machine || '?' }} (you)</strong>
        <span v-else>{{ item.machine || '?' }}</span>
      </template>
      <template v-slot:item.lastSeenAt="{item}">
        <span :title="`${formatTime(item.lastSeenAt)}: ${item.lastPath}`">
          {{ formatDuration(now - item.lastSeenAt) }} ago
        </span>
      </template>
      <template v-slot:item.firstSeenAt="{item}">
        <span :title="`since ${formatTime(item.firstSeenAt)}, ${item.requests} calls`">
          {{ formatDuration(item.lastSeenAt - item.firstSeenAt) }}
        </span>
      </template>
      <template v-slot:item.userAgent="{item}">
        <span class="caption" :title="item.userAgent">{{ browserOf(item.userAgent) }}</span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import moment from 'moment';
import api from '@/api';

/**
 * Who is using the kitchen, from the api's record of each machine's calls
 * (fed-nutrition-api services/activeUsers.ts).
 */
export default {
  name: 'ActiveUsers',
  data() {
    return {
      users: null,
      activeMs: 10 * 60 * 1000,
      awayMs: 60 * 60 * 1000,
      serverOffset: 0,
      loading: false,
      error: null,
      showGone: false,
      now: Date.now(),
      refreshSeconds: 30,
      refreshTimer: null,
      clockTimer: null,
      statusColors: {active: 'green', away: 'orange', gone: 'grey'},
      headers: [
        {text: 'Status', value: 'status'},
        {text: 'Machine', value: 'machine'},
        {text: 'Email', value: 'email'},
        {text: 'Role', value: 'roleName'},
        {text: 'IP', value: 'ip'},
        {text: 'Last call', value: 'lastSeenAt'},
        {text: 'Active for', value: 'firstSeenAt', sort: (a, b) => b - a},
        {text: 'Browser', value: 'userAgent'},
      ],
    };
  },
  computed: {
    thisBrowserId() {
      try {
        return window.localStorage.getItem('k.fed-browser-id');
      } catch (e) {
        return null;
      }
    },
    allRows() {
      return (this.users || []).map(u => ({
        ...u,
        key: `${u.userId}|${u.fedBrowserId || u.ip}|${u.firstSeenAt}`,
        isThisMachine: !!this.thisBrowserId && u.fedBrowserId === this.thisBrowserId,
      }));
    },
    rows() {
      return this.showGone ? this.allRows : this.allRows.filter(r => r.status !== 'gone');
    },
    counts() {
      const counts = {active: 0, away: 0, gone: 0};
      for (const row of this.allRows) counts[row.status]++;
      return counts;
    },
  },
  mounted() {
    this.fetchUsers();
    this.refreshTimer = setInterval(this.fetchUsers, this.refreshSeconds * 1000);
    this.clockTimer = setInterval(() => this.now = Date.now() + this.serverOffset, 1000);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    clearInterval(this.clockTimer);
  },
  methods: {
    async fetchUsers() {
      if (this.loading) return;
      this.loading = true;
      try {
        const {data} = await api.get('v2/active-users');
        this.users = data.users;
        this.activeMs = data.activeMs;
        this.awayMs = data.awayMs;
        // durations are worked out against the api's clock, not this machine's
        this.serverOffset = data.now - Date.now();
        this.now = data.now;
        this.error = null;
      } catch (e) {
        this.error = `Could not list the active users: ${(e.response && e.response.status) || e.message}`;
      } finally {
        this.loading = false;
      }
    },
    minutes(ms) {
      return Math.round(ms / 60000);
    },
    formatTime(ms) {
      return ms ? moment(ms).format('ddd MMM D, h:mm:ss a') : '';
    },
    formatDuration(ms) {
      if (!(ms >= 0)) return '';
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ${minutes % 60}m`;
      return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    },
    browserOf(userAgent) {
      if (!userAgent) return '';
      const browser = /Edg\//.test(userAgent) ? 'Edge'
        : /Chrome\//.test(userAgent) ? 'Chrome'
          : /Firefox\//.test(userAgent) ? 'Firefox'
            : /Safari\//.test(userAgent) ? 'Safari' : 'Other';
      const os = /Windows/.test(userAgent) ? 'Windows'
        : /iPhone|iPad/.test(userAgent) ? 'iOS'
          : /Mac OS X/.test(userAgent) ? 'Mac'
            : /Android/.test(userAgent) ? 'Android'
              : /Linux|CrOS/.test(userAgent) ? 'Linux' : '';
      return os ? `${browser} on ${os}` : browser;
    },
  },
};
</script>
